import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { CustomButton } from "../../../components/CustomButton";
import { showCustomToast } from "../../../components/CustomToast";
import { PageHeaderContainer } from "../../../components/PageHeaderContainer";
import { useHandleLeavePrompt } from "../../../hooks/useHandleLeavePrompt";
import { useModal } from "../../../hooks/useModal";
import { LOCATION_DETAIL_PAGE_PATH } from "../../../middleware/routes";
import {
  cleanUpLocationDetailVisits,
  getLocationDetailVisitsThunk,
  setLocationDetailLocationId,
} from "../../../reducers/locationDetailReducer";
import { cleanUpLocationsList, getLocationsListThunk } from "../../../reducers/locationsReducer";
import { saveVisitEdit } from "../../../reducers/visitEditReducer";
import { getCollectionYardPaidVisitVerification } from "../../../selectors/collectionYard";
import { getLocationDetailLocation, getLocationDetailLocationId } from "../../../selectors/locationDetail";
import {
  getVisitEditCanSave,
  getVisitEditEvents,
  getVisitEditIsTouched,
  getVisitEditRemovedEventsIds,
  getVisitEditTotalPriceForFinishedEvents,
  getVisitEditVisitId,
} from "../../../selectors/visitEdit";
import { SearchParams } from "../../../types/searchParams";
import { getRouteUrlWithParams } from "../../../utils/route";
import { LeaveConfirmationModal } from "../LeaveConfirmationModal/LeaveConfirmationModal";
import { VERIFY_PAID_VISIT_MODAL_ID, VerifyPaidVisitModal } from "../VerifyPaidVisitModal";

import styles from "./VisitEditPageHeaderComponent.module.scss";

const SAVE_VISIT_BUTTON_ID = "save-visit-button";

export const VisitEditPageHeaderComponent: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [isUpdating, setIsUpdating] = useState(false);

  const locationId = useSelector(getLocationDetailLocationId);
  const visitId = useSelector(getVisitEditVisitId);
  const events = useSelector(getVisitEditEvents);
  const removedEventsIds = useSelector(getVisitEditRemovedEventsIds);
  const canSave = useSelector(getVisitEditCanSave);
  const { name } = useSelector(getLocationDetailLocation);
  const currentDate = new Date().toLocaleDateString();
  const totalPriceForFinishedEvents = useSelector(getVisitEditTotalPriceForFinishedEvents);
  const collectionYardPaidVisitVerification = useSelector(getCollectionYardPaidVisitVerification);

  const verifyPaidVisitModal = useModal();

  const handlePlateNumberContinue = async (plateNumber: string, qrCodeToken?: string) => {
    handleSave(plateNumber, qrCodeToken, true);
  };

  const isTouched = useSelector(getVisitEditIsTouched);
  const leaveConfirmationModal = useHandleLeavePrompt(isTouched && !isUpdating, [
    VERIFY_PAID_VISIT_MODAL_ID,
    SAVE_VISIT_BUTTON_ID,
  ]);
  const backPath = getRouteUrlWithParams(LOCATION_DETAIL_PAGE_PATH, { [SearchParams.LocationId]: locationId });

  const handleSave = async (plateNumber?: string, qrResult?: string, plateNumberPromptDisplayed?: boolean) => {
    try {
      if (totalPriceForFinishedEvents > 0) {
        if (collectionYardPaidVisitVerification && !qrResult) {
          verifyPaidVisitModal.toggleModalVisibility();
          return;
        }
        if (!collectionYardPaidVisitVerification && !plateNumberPromptDisplayed) {
          verifyPaidVisitModal.toggleModalVisibility();
          return;
        }
      }

      setIsUpdating(true);
      await saveVisitEdit(events, removedEventsIds, locationId, visitId, plateNumber, qrResult);

      dispatch(cleanUpLocationDetailVisits());
      dispatch(setLocationDetailLocationId(Number(locationId)));
      dispatch(getLocationDetailVisitsThunk());
      dispatch(cleanUpLocationsList());
      dispatch(getLocationsListThunk());
      history.push(backPath);

      showCustomToast(t("saveSuccess"), { type: "success" });
    } catch (error: any) {
      if (error?.response?.data?.message) {
        showCustomToast(t(`errors.${error.response.data.message}`), { type: "error" });
        return;
      }

      showCustomToast(t("saveError"), { type: "error" });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <PageHeaderContainer
        title={currentDate}
        subtitle={name}
        backTo={backPath}
        RightComponent={
          <CustomButton
            id={SAVE_VISIT_BUTTON_ID}
            className={styles.saveBtn}
            text={t("save")}
            onClick={() => handleSave()}
            disabled={isUpdating || !canSave}
          />
        }
      />
      <LeaveConfirmationModal
        isModalVisible={leaveConfirmationModal.isModalVisible}
        toggleModalVisibility={leaveConfirmationModal.toggleModalVisibility}
        handleContinue={leaveConfirmationModal.handleContinue}
      />
      <VerifyPaidVisitModal
        isModalVisible={verifyPaidVisitModal.isModalVisible}
        toggleModalVisibility={verifyPaidVisitModal.toggleModalVisibility}
        handleContinue={handlePlateNumberContinue}
      />
    </>
  );
};
